import { Modal, Input, type InputRef } from "antd";
import { EditOutlined, CloseOutlined } from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { useMenuModifiers } from "../../../../hooks/useMenu";
import type { Item, Modifier } from "../../../../api/menu";
import { getItemName, getTranslation } from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles, KbdChip } from "../modalPrimitives";
import { useViewport } from "../../../../hooks/useViewport";
import { AddonCheckbox } from "./ModifyItemModal";
import { QuantityStepper } from "./QuantityStepper";
import { useQuantityInput } from "./useQuantityInput";

interface EditModifiersModalProps {
  item: Item | null;
  open: boolean;
  /** Текущите количество/модификатори/коментар на реда — префилват модала
   *  при отваряне. */
  initial: { quantity: number; modifiers: number[]; comment: string } | null;
  /** Къде е фокусът при отваряне на десктоп: „comment" от молива в „Преглед
   *  на поръчката", „quantity" от молива на реда/картата в каталога. */
  focusField?: "comment" | "quantity";
  onClose: () => void;
  onSave: (data: {
    quantity: number;
    modifiers: number[];
    comment: string;
  }) => void;
}

/**
 * Модал за редакция на количество + модификатори + коментар на вече маркиран
 * ред — моливът в „Преглед на поръчката" и в степера на каталога (списък/грид).
 * Същият стил като ModifyItemModal, но БЕЗ добавки — те са фиксирани при
 * маркирането.
 */
export const EditModifiersModal = ({
  item,
  open,
  initial,
  focusField = "comment",
  onClose,
  onSave,
}: EditModifiersModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const [selectedModifiers, setSelectedModifiers] = useState<number[]>([]);
  const [comment, setComment] = useState("");
  const {
    quantity,
    quantityInput,
    setQuantity,
    handleQuantityInputChange,
    decrement,
    increment,
  } = useQuantityInput();
  // На десктоп полето по focusField получава фокуса щом модалът се отвори —
  // от прегледа човекът идва да пише коментар, от каталога — да смени
  // количеството. Фокусът е след анимацията на antd Modal (afterOpenChange),
  // иначе диалогът си го взема обратно. На телефон/таблет не — екранната
  // клавиатура би покрила модификаторите.
  const commentRef = useRef<TextAreaRef>(null);
  const quantityRef = useRef<InputRef>(null);

  const allModifiers = useMenuModifiers();
  const modifiers: Modifier[] = item
    ? allModifiers.filter((m) => item.modifierIds.includes(m.id))
    : [];

  // Модалът се префилва от реда при всяко отваряне (не пази старо състояние).
  useEffect(() => {
    if (!open) return;
    setQuantity(initial?.quantity ?? 1);
    setSelectedModifiers(initial?.modifiers ?? []);
    setComment(initial?.comment ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 0 (или празно поле) не се записва — редът се маха от степера, не оттук
  const canSave = quantity > 0;

  const handleSave = () => {
    if (!canSave) return;
    onSave({ quantity, modifiers: selectedModifiers, comment: comment.trim() });
    onClose();
  };

  // Keyboard shortcuts — Enter запазва, Esc затваря (както в ModifyItemModal).
  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      ) {
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDesktop, quantity, selectedModifiers, comment]);

  if (!item) return null;

  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const width = isMobile ? undefined : isTablet ? 520 : 640;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={(opened) => {
        if (!opened || !isDesktop) return;
        if (focusField === "quantity") quantityRef.current?.focus({ cursor: "all" });
        else commentRef.current?.focus({ cursor: "end" });
      }}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={{
        ...modalStyles,
        content: { ...modalStyles.content, borderRadius: 20 },
      }}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
        {/* Хедър — икон-chip + име на продукта + единична цена + close chip */}
        <div
          className={`flex items-center gap-3 border-b border-pos-line2 ${
            isNarrow ? "px-4 py-3.5" : "px-[22px] py-4"
          }`}
        >
          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[11px] bg-pos-ready-bg text-pos-accent">
            <EditOutlined className="text-[20px]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-lg font-extrabold leading-tight text-pos-ink">
              {itemName}
            </h2>
            <p className="m-0 mt-0.5 font-pos-mono text-[13px] text-pos-muted">
              {item.price.toFixed(2)} EUR
            </p>
          </div>
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="grid h-[38px] w-[38px] flex-shrink-0 cursor-pointer place-items-center rounded-[10px] border border-pos-line2 bg-pos-card text-pos-dim transition-colors hover:text-pos-ink"
          >
            <CloseOutlined className="text-[15px]" />
          </button>
        </div>

        <div
          className={`flex min-h-0 flex-col gap-[18px] overflow-y-auto ${
            isNarrow ? "p-4" : "px-6 py-5"
          }`}
        >
          <QuantityStepper
            quantity={quantity}
            quantityInput={quantityInput}
            onInputChange={handleQuantityInputChange}
            onDecrement={decrement}
            onIncrement={increment}
            isMobile={isMobile}
            inputRef={quantityRef}
            // Фокусът идва тук от каталога — Enter запазва (shortcut ⏎)
            onPressEnter={handleSave}
          />

          {/* Modifiers — редове с чекбокс, същият език като в ModifyItemModal */}
          {modifiers.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
                {t("ordering.modifiers")}
              </span>
              <div className="flex flex-col gap-1.5">
                {modifiers.map((modifier) => {
                  const modifierName = getTranslation(
                    modifier.translations,
                    i18n.language,
                    modifier.slug,
                  );
                  const isSelected = selectedModifiers.includes(modifier.id);
                  return (
                    <button
                      type="button"
                      key={modifier.id}
                      onClick={() => {
                        setSelectedModifiers((prev) =>
                          prev.includes(modifier.id)
                            ? prev.filter((id) => id !== modifier.id)
                            : [...prev, modifier.id],
                        );
                      }}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-[11px] transition-colors ${
                        isSelected
                          ? "border-pos-accent bg-pos-marked"
                          : "border-pos-line3 bg-pos-card"
                      }`}
                    >
                      <AddonCheckbox checked={isSelected} />
                      <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-pos-ink">
                        {modifierName}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Comment */}
          <div className="flex flex-col gap-2.5">
            <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
              {t("ordering.itemComment")}
            </span>
            <Input.TextArea
              ref={commentRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("ordering.itemCommentPlaceholder")}
              // Enter в коментара тригерва „Запази" (shortcut ⏎), вместо нов
              // ред; Shift+Enter остава за нов ред.
              onPressEnter={(e) => {
                if (e.shiftKey) return;
                e.preventDefault();
                handleSave();
              }}
              autoSize={{ minRows: 1, maxRows: 5 }}
              maxLength={200}
              className="!resize-none !rounded-xl !border-pos-ctl !bg-pos-panel !px-[15px] !py-[13px] !text-[15px] !leading-normal !text-pos-ink placeholder:!text-pos-muted"
            />
          </div>
        </div>

        {/* Футър — ghost „Назад" + primary „Запази" */}
        <div
          className={`flex gap-2.5 border-t border-pos-line2 ${
            isNarrow ? "px-4 py-3" : "px-[22px] py-3.5"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className="relative h-[54px] flex-1 cursor-pointer rounded-[14px] border border-pos-ctl bg-pos-card text-[15px] font-semibold text-pos-ink2 transition-colors hover:bg-pos-panel"
          >
            {t("common.back")}
            {isDesktop && (
              <KbdChip shortcut="Esc" colorClass="text-pos-faint" />
            )}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="relative h-[54px] flex-[2] cursor-pointer rounded-[14px] bg-pos-accent text-base font-bold text-white transition-colors hover:bg-pos-accent-ink disabled:cursor-not-allowed disabled:opacity-45"
          >
            {t("common.save")}
            {isDesktop && (
              // ⏎ е Unicode глиф, който рендира по-дребно от латиница — бъмпваме
              // размера, за да съвпадне визуално с "Esc" на съседния бутон.
              <span className="absolute right-2 top-[3px] font-pos-mono text-[14px] font-semibold leading-none text-white/60">
                ⏎
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
